// Contact Component (Section in Home Page)
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { HiMail, HiPhone, HiLocationMarker, HiGlobeAlt, HiPaperAirplane } from 'react-icons/hi';

const Contact = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  const contactInfo = [
    { icon: HiMail, title: 'Email', details: 'hr@efatechsolutions.com', link: 'mailto:hr@efatechsolutions.com' },
    { icon: HiPhone, title: 'Phone', details: '+91 9515212971', link: 'tel:+91 9515212971' },
    { icon: HiLocationMarker, title: 'Address', details: 'Karimnagar IT Towers - opposite Ujwala Park, Rampur, Karimnagar, Telangana 505001', link: 'https://share.google/0J3Ycvll2MP4keswc' },
    { icon: HiGlobeAlt, title: 'Website', details: 'www.efatechsolutions.com', link: 'https://efatechsolutions.com' }
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatusMessage("");

    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(`${API_BASE_URL}/api/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatusMessage("Message sent successfully!");
        setFormData({ name: "", email: "", subject: "", message: "" });
      } else {
        const result = await response.json();
        setStatusMessage(result.message || "Failed to send message.");
      }
    } catch (error) {
      console.error(error);
      setStatusMessage("Failed to send message. Please check your connection.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.2 } }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <motion.div ref={ref} variants={containerVariants} initial="hidden" animate={inView ? "visible" : "hidden"} className="text-center mb-16">
          <motion.h2 variants={itemVariants} className="text-4xl md:text-5xl font-bold text-neutral-900 mb-6">Get In <span className="text-primary-500">Touch</span></motion.h2>
          <motion.p variants={itemVariants} className="text-xl text-neutral-700 max-w-3xl mx-auto">Ready to start your next project? We'd love to hear from you. Send us a message and we'll respond as soon as possible.</motion.p>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" animate={inView ? "visible" : "hidden"} className="grid lg:grid-cols-2 gap-16">
          <motion.div variants={itemVariants}>
            <h3 className="text-2xl font-bold text-neutral-900 mb-8">Contact Information</h3>
            <div className="space-y-6">
              {contactInfo.map((info, index) => (
                <motion.a key={index} href={info.link} variants={itemVariants} whileHover={{ scale: 1.02 }} className="flex items-center space-x-4 p-4 bg-neutral-50 rounded-xl hover:bg-primary-50 transition-all duration-300 group">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center group-hover:bg-primary-200 transition-colors">
                    <info.icon className="w-6 h-6 text-primary-500" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-neutral-900">{info.title}</h4>
                    <p className="text-neutral-700">{info.details}</p>
                  </div>
                </motion.a>
              ))}
            </div>
          </motion.div>

          <motion.div variants={itemVariants}>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Form Inputs */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name-component" className="block text-sm font-medium text-neutral-700 mb-2">Your Name</label>
                  <input type="text" id="name-component" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300" placeholder="John Doe" />
                </div>
                <div>
                  <label htmlFor="email-component" className="block text-sm font-medium text-neutral-700 mb-2">Email Address</label>
                  <input type="email" id="email-component" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300" placeholder="john@example.com" />
                </div>
              </div>
              <div>
                <label htmlFor="subject-component" className="block text-sm font-medium text-neutral-700 mb-2">Subject</label>
                <input type="text" id="subject-component" name="subject" value={formData.subject} onChange={handleChange} required className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300" placeholder="Project Discussion" />
              </div>
              <div>
                <label htmlFor="message-component" className="block text-sm font-medium text-neutral-700 mb-2">Message</label>
                <textarea id="message-component" name="message" value={formData.message} onChange={handleChange} required rows={6} className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 resize-none" placeholder="Tell us about your project..."></textarea>
              </div>

              {statusMessage && (
                <p className="text-center text-neutral-600">{statusMessage}</p>
              )}

              <motion.button type="submit" disabled={isSubmitting} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className={`w-full bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-semibold text-lg transition-all duration-300 flex items-center justify-center space-x-2 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}>
                {isSubmitting ? (<div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />) : (<><span>Send Message</span><HiPaperAirplane className="w-5 h-5" /></>)}
              </motion.button>

              {/* Added note about alternative contact method */}
              <p className="text-center text-neutral-600 mt-4">
                Alternatively, you can email us directly at <a href="mailto:hr@efatechsolutions.com" className="text-primary-500 hover:underline">hr@efatechsolutions.com</a>
              </p>
            </form>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
